import { Team } from "@redotech/redo-model/team";
import {
  AttachmentStrategy,
  doesAttachmentStrategyRenderInCart,
  isAttachmentStrategyCheckoutExtensionEnabled,
} from "@redotech/redo-storefront-common/attachment-strategy";
import { InfoModalVersion } from "@redotech/redo-storefront-common/info-modal-version";
import { InsertionPosition } from "@redotech/redo-storefront-common/insertion-position";
import { InputSchema, groupInput, input, listInput } from "@redotech/ui/form";
import {
  nonEmptyValidator,
  nonWhitespaceValidator,
} from "@redotech/ui/validators";
import { optionalEqual, urlEqual } from "@redotech/util/equal";
import cloneDeep from "lodash/cloneDeep";

export const cartCountModificationInput = groupInput({
  id: input<symbol>({ equal: () => true }),
  selector: input<string>({ validator: nonEmptyValidator }),
  search: input<string>({ validator: nonEmptyValidator }),
  replace: input<string>({ validator: nonEmptyValidator }),
});

const cartCountModificationDefault = (
  id: symbol,
): InputSchema.Value<typeof cartCountModificationInput> => ({
  id,
  selector: "",
  search: "\\d+",
  replace: "%count%",
});

const productCustomizations = groupInput({
  productTitle: input<string>(),
  toggleMainText: input<string>(),
  toggleSubTextPositive: input<string>(),
  toggleSubTextNegative: input<string>(),
  sku: input<string>(),
});

export type ProductCustomizations = InputSchema.Input<
  typeof productCustomizations
>;

export const storefrontForm = groupInput({
  attachmentStrategy: input<AttachmentStrategy>(),
  shopifyProductSource: input<"supplier" | "store">(),
  toggleSubtextEnabled: input<boolean>(),
  BothProductOptions: productCustomizations,
  cartToggle: groupInput({
    useShadowRootInCart: input<boolean>(),
    cartShadowRootSelector: input<string>(),
    selector: input<string>({ validator: nonWhitespaceValidator }),
    placement: input<InsertionPosition>(),
    clickSelector: input<string>(),
    toggleOnColor: input<string>(),
    toggleOffColor: input<string>(),
    toggleCircleColor: input<string>(),
    cartReplaceItems: input<boolean>(),
  }),
  checkbox: groupInput({
    default: input<boolean>(),
    selector: input<string>({ validator: nonWhitespaceValidator }),
    productName: input<string>(),
    text: input<string>(),
    html: input<string>(),
    hideCheckboxBranding: input<boolean>(),
    hideCheckboxPrice: input<boolean>(),
    textAfterRedo: input<string>(),
  }),
  checkboxInfoModal: groupInput({
    text1: input<string>(),
    title1: input<string>(),
    text2: input<string>(),
    title2: input<string>(),
    text3: input<string>(),
    title3: input<string>(),
    logo: input<URL | undefined>({ equal: optionalEqual(urlEqual) }),
    cartIconEnabled: input<boolean>(),
    checkoutIconEnabled: input<boolean>(),
    newLogo: input<URL | undefined>({ equal: optionalEqual(urlEqual) }),
    sideImage: input<URL | undefined>({ equal: optionalEqual(urlEqual) }),
    version: input<InfoModalVersion>(),
  }),
  hideRedoProduct: groupInput({
    enabled: input<boolean>(),
    productSelector: input<string>(),
    cartCountModifications: listInput(
      () => cartCountModificationInput,
      cartCountModificationDefault,
      (i) => i.id,
    ),
    cartPriceModifications: listInput(
      () => cartCountModificationInput,
      cartCountModificationDefault,
      (i) => i.id,
    ),
    cartDiscountModifications: listInput(
      () => cartCountModificationInput,
      cartCountModificationDefault,
      (i) => i.id,
    ),
  }),
  splitProducts: input<boolean>(),
  uniqueVariantSKU: input<boolean>(),
  indexBasedUniqueVariantStartingAt: input<number | undefined>(),
  redoVariantBaseName: input<string>(),
  packageProtectionAutoCheck: input<boolean>(),
  PackageProtectionProductOptions: productCustomizations,
  sku2: input<string>(),
  returnProductOptions: productCustomizations,
  customPdpCss: input<string>(),
  customToggleCss: input<string>(),
  usingCartAndCheckoutToggle: input<boolean>(),
  autoNavigateToCheckoutAfterTimeout: input<boolean>(),
  trackRedoProductInventory: input<boolean>(),
  lockProduct: input<boolean>(),
  allowNonRedoVendorOnProduct: input<boolean>(),
});

export type StorefrontForm = InputSchema.Input<typeof storefrontForm>;
export type StorefrontValue = InputSchema.Value<typeof storefrontForm>;

export const storefrontDefault: StorefrontValue = {
  attachmentStrategy: AttachmentStrategy.CHECKBOX,
  shopifyProductSource: "store",
  toggleSubtextEnabled: true,
  sku2: "",
  BothProductOptions: {
    productTitle: "",
    toggleMainText: "",
    toggleSubTextPositive: "",
    toggleSubTextNegative: "",
    sku: "",
  },
  checkbox: {
    default: true,
    selector: "",
    text: "",
    html: "",
    productName: "",
    hideCheckboxBranding: false,
    hideCheckboxPrice: false,
    textAfterRedo: "",
  },
  checkboxInfoModal: {
    text1: "",
    title1: "",
    text2: "",
    title2: "",
    text3: "",
    title3: "",
    logo: undefined,
    cartIconEnabled: false,
    checkoutIconEnabled: false,
    newLogo: undefined,
    sideImage: undefined,
    version: InfoModalVersion.V1,
  },
  cartToggle: {
    useShadowRootInCart: false,
    cartShadowRootSelector: "",
    selector: "",
    placement: InsertionPosition.BEFORE,
    clickSelector: "",
    toggleOnColor: "",
    toggleOffColor: "",
    toggleCircleColor: "",
    cartReplaceItems: false,
  },
  hideRedoProduct: {
    enabled: false,
    productSelector: "",
    cartCountModifications: [],
    cartPriceModifications: [],
    cartDiscountModifications: [],
  },
  packageProtectionAutoCheck: true,
  PackageProtectionProductOptions: {
    productTitle: "",
    toggleMainText: "",
    toggleSubTextPositive: "",
    toggleSubTextNegative: "",
    sku: "",
  },
  returnProductOptions: {
    productTitle: "",
    toggleMainText: "",
    toggleSubTextPositive: "",
    toggleSubTextNegative: "",
    sku: "",
  },
  uniqueVariantSKU: false,
  indexBasedUniqueVariantStartingAt: undefined,
  redoVariantBaseName: "",
  splitProducts: false,
  customPdpCss: "",
  customToggleCss: "",
  usingCartAndCheckoutToggle: false,
  autoNavigateToCheckoutAfterTimeout: true,
  trackRedoProductInventory: false,
  lockProduct: false,
  allowNonRedoVendorOnProduct: false,
};

export function storefrontTeamToFormValue(team: Team) {
  return {
    attachmentStrategy:
      team.settings.shopifyExtension?.attachmentStrategy ??
      AttachmentStrategy.CHECKOUT_BUTTON_PRODUCT,
    toggleSubtextEnabled:
      team.settings.shopifyExtension?.toggleSubtextEnabled ?? true,
    BothProductOptions: {
      productTitle:
        team.settings.shopifyExtension?.bothProductNameOverride || "",
      toggleMainText:
        team.settings.shopifyExtension?.toggleTextOptions?.bothProductToggle
          .titleText || "",
      toggleSubTextPositive:
        team.settings.shopifyExtension?.toggleTextOptions?.bothProductToggle
          .positiveSubtext || "",
      toggleSubTextNegative:
        team.settings.shopifyExtension?.toggleTextOptions?.bothProductToggle
          .negativeSubtext || "",
      sku: team.settings.combinedSKU || "",
    },
    cartToggle: {
      useShadowRootInCart: team.settings.useShadowRootInCart || false,
      cartShadowRootSelector: team.settings.cartShadowRootSelector || "",
      selector: team.settings.cartToggleSelector || "",
      placement: team.settings.cartTogglePlacement || InsertionPosition.BEFORE,
      clickSelector: team.settings.cartClickListenerSelector || "",
      toggleOnColor: team.settings.toggleOnColor || "",
      toggleOffColor: team.settings.toggleOffColor || "",
      toggleCircleColor: team.settings.toggleCircleColor || "",
      cartReplaceItems: team.settings.cartReplaceItems ?? false,
    },
    checkbox: {
      default: team.settings.redoAutoCheck,
      html: team.settings.html || "",
      selector: team.settings.checkboxSelector || "",
      productName: team.settings.productName || "",
      text: team.settings.checkboxText || "",
      hideCheckboxBranding: team.settings.hideCheckboxBranding || false,
      hideCheckboxPrice: team.settings.hideCheckboxPrice || false,
      textAfterRedo: team.settings.checkboxTextAfterRedo || "",
    },
    checkboxInfoModal: {
      text1: team.settings.modalTextAdjustments?.text1 || "",
      title1: team.settings.modalTextAdjustments?.title1 || "",
      text2: team.settings.modalTextAdjustments?.text2 || "",
      title2: team.settings.modalTextAdjustments?.title2 || "",
      text3: team.settings.modalTextAdjustments?.text3 || "",
      title3: team.settings.modalTextAdjustments?.title3 || "",
      logo: team.settings.modalLogo
        ? new URL(team.settings.modalLogo)
        : undefined,
      cartIconEnabled: team.settings.infoModal?.cartIconEnabled ?? false,
      checkoutIconEnabled:
        team.settings.infoModal?.checkoutIconEnabled ?? false,
      newLogo: team.settings.infoModal?.logo
        ? new URL(team.settings.infoModal.logo)
        : undefined,
      sideImage: team.settings.infoModal?.sideImage
        ? new URL(team.settings.infoModal.sideImage)
        : undefined,
      version: team.settings.infoModal?.version || InfoModalVersion.V1,
    },
    hideRedoProduct: {
      enabled: !!team.settings.shopifyExtension?.hideRedoProduct?.enabled,
      productSelector:
        team.settings.shopifyExtension?.hideRedoProduct?.productSelector || "",
      cartCountModifications:
        team.settings.shopifyExtension?.hideRedoProduct?.cartCountModifications?.map(
          (x) => ({ id: Symbol(), ...x }),
        ) || [],
      cartPriceModifications:
        team.settings.shopifyExtension?.hideRedoProduct?.cartPriceModifications?.map(
          (x) => ({ id: Symbol(), ...x }),
        ) || [],
      cartDiscountModifications:
        team.settings.shopifyExtension?.hideRedoProduct?.cartDiscountModifications?.map(
          (x) => ({ id: Symbol(), ...x }),
        ) || [],
    },
    packageProtectionAutoCheck:
      team.settings.packageProtectionAutoCheck ?? team.settings.redoAutoCheck,
    PackageProtectionProductOptions: {
      productTitle:
        team.settings.shopifyExtension?.packageProtectionNameOverride || "",
      toggleMainText:
        team.settings.shopifyExtension?.toggleTextOptions
          ?.packageProtectionToggle.titleText || "",
      toggleSubTextPositive:
        team.settings.shopifyExtension?.toggleTextOptions
          ?.packageProtectionToggle.positiveSubtext || "",
      toggleSubTextNegative:
        team.settings.shopifyExtension?.toggleTextOptions
          ?.packageProtectionToggle.negativeSubtext || "",
      sku: team.settings.packageProtectionSKU || "",
    },
    returnProductOptions: {
      productTitle: team.settings.shopifyExtension?.returnNameOverride || "",
      toggleMainText:
        team.settings.shopifyExtension?.toggleTextOptions?.returnToggle
          .titleText || "",
      toggleSubTextPositive:
        team.settings.shopifyExtension?.toggleTextOptions?.returnToggle
          .positiveSubtext || "",
      toggleSubTextNegative:
        team.settings.shopifyExtension?.toggleTextOptions?.returnToggle
          .negativeSubtext || "",
      sku: team.settings.returnsSKU || "",
    },
    shopifyProductSource: team.shopifyCoverageProductSource!,
    sku2: team.settings.sku2 || "",
    splitProducts: team.settings.packageProtection?.splitProducts || false,
    customPdpCss: team.settings.shopifyExtension?.customPdpCss || "",
    customToggleCss: team.settings.shopifyExtension?.customToggleCss || "",
    uniqueVariantSKU: team.settings.uniqueVariantSKU ?? false,
    indexBasedUniqueVariantStartingAt:
      team.settings.indexBasedUniqueVariantStartingAt,
    usingCartAndCheckoutToggle:
      team.settings.shopifyExtension?.usingCartAndCheckoutToggle ?? false,
    autoNavigateToCheckoutAfterTimeout:
      team.settings.shopifyExtension?.autoNavigateToCheckoutAfterTimeout ??
      true,
    redoVariantBaseName:
      team.settings.shopifyExtension?.redoVariantBaseName || "",
    trackRedoProductInventory:
      team.settings.shopifyExtension?.trackRedoProductInventory ?? false,
    lockProduct: team.settings.shopifyExtension?.lockProduct ?? false,
    allowNonRedoVendorOnProduct:
      team.settings.shopifyExtension?.allowNonRedoVendorOnProduct ?? false,
  };
}

export const storefrontFormValueToTeam = ({
  formValue,
  prevTeamValue,
}: {
  formValue: { storefront: StorefrontValue };
  prevTeamValue: Team;
}): Team => {
  const team = cloneDeep(prevTeamValue);

  team.settings.checkboxText = formValue.storefront.checkbox.text || undefined;
  team.settings.html = formValue.storefront.checkbox.html || undefined;
  team.settings.redoAutoCheck = formValue.storefront.checkbox.default;
  team.settings.checkboxSelector =
    formValue.storefront.checkbox.selector || undefined;
  team.settings.checkboxTextAfterRedo =
    formValue.storefront.checkbox.textAfterRedo || undefined;
  team.settings.hideCheckboxBranding =
    formValue.storefront.checkbox.hideCheckboxBranding;
  team.settings.hideCheckboxPrice =
    formValue.storefront.checkbox.hideCheckboxPrice;
  team.settings.useShadowRootInCart =
    formValue.storefront.cartToggle.useShadowRootInCart;
  team.settings.cartShadowRootSelector =
    formValue.storefront.cartToggle.cartShadowRootSelector;
  team.settings.cartToggleEnabled =
    doesAttachmentStrategyRenderInCart[formValue.storefront.attachmentStrategy];
  team.settings.cartToggleSelector =
    formValue.storefront.cartToggle.selector || undefined;
  team.settings.cartTogglePlacement =
    formValue.storefront.cartToggle.placement || undefined;
  team.settings.cartClickListenerSelector =
    formValue.storefront.cartToggle.clickSelector || undefined;
  team.settings.toggleOnColor =
    formValue.storefront.cartToggle.toggleOnColor || "";
  team.settings.toggleOffColor =
    formValue.storefront.cartToggle.toggleOffColor || "";
  team.settings.toggleCircleColor =
    formValue.storefront.cartToggle.toggleCircleColor || "";
  team.settings.cartReplaceItems =
    formValue.storefront.cartToggle.cartReplaceItems;
  team.settings.productName =
    formValue.storefront.checkbox.productName || undefined;

  team.shopifyCoverageProductSource = formValue.storefront.shopifyProductSource;

  team.settings.packageProtection = {
    enabled: team.settings.packageProtection?.enabled ?? false,
    minPrice: team.settings.packageProtection?.minPrice ?? 1,
    maxPrice: team.settings.packageProtection?.maxPrice ?? 15,
    percentage: team.settings.packageProtection?.percentage ?? 0,
    ...team.settings.packageProtection,
    splitProducts: formValue.storefront.splitProducts,
  };

  team.settings.returnsSKU =
    formValue.storefront.returnProductOptions.sku || "";
  team.settings.packageProtectionSKU =
    formValue.storefront.PackageProtectionProductOptions.sku || "";
  team.settings.combinedSKU = formValue.storefront.BothProductOptions.sku || "";
  team.settings.uniqueVariantSKU =
    formValue.storefront.uniqueVariantSKU ?? false;
  team.settings.indexBasedUniqueVariantStartingAt =
    formValue.storefront.indexBasedUniqueVariantStartingAt;

  team.settings.shopifyExtension = {
    ...team.settings.shopifyExtension,
    customizationVisible:
      team.settings.shopifyExtension?.customizationVisible ?? false,
    customizeCartItem:
      team.settings.shopifyExtension?.customizeCartItem ?? false,
    brandPrefix: team.settings.shopifyExtension?.brandPrefix ?? "",
    bothProductNameOverride:
      formValue.storefront.BothProductOptions.productTitle ?? "",
    packageProtectionNameOverride:
      formValue.storefront.PackageProtectionProductOptions.productTitle ?? "",
    returnNameOverride:
      formValue.storefront.returnProductOptions.productTitle ?? "",
    redoVariantBaseName: formValue.storefront.redoVariantBaseName ?? "",
    toggleTextOptions: {
      returnToggle: {
        titleText:
          formValue.storefront.returnProductOptions.toggleMainText ?? "",
        positiveSubtext:
          formValue.storefront.returnProductOptions.toggleSubTextPositive ?? "",
        negativeSubtext:
          formValue.storefront.returnProductOptions.toggleSubTextNegative ?? "",
      },
      packageProtectionToggle: {
        titleText:
          formValue.storefront.PackageProtectionProductOptions.toggleMainText ??
          "",
        positiveSubtext:
          formValue.storefront.PackageProtectionProductOptions
            .toggleSubTextPositive ?? "",
        negativeSubtext:
          formValue.storefront.PackageProtectionProductOptions
            .toggleSubTextNegative ?? "",
      },
      bothProductToggle: {
        titleText: formValue.storefront.BothProductOptions.toggleMainText ?? "",
        positiveSubtext:
          formValue.storefront.BothProductOptions.toggleSubTextPositive ?? "",
        negativeSubtext:
          formValue.storefront.BothProductOptions.toggleSubTextNegative ?? "",
      },
    },
    attachmentStrategy: formValue.storefront.attachmentStrategy,
    toggleSubtextEnabled: formValue.storefront.toggleSubtextEnabled,
    hideRedoProduct: {
      enabled: formValue.storefront.hideRedoProduct.enabled,
      productSelector: formValue.storefront.hideRedoProduct.productSelector,
      checkoutButtonSelector:
        team.settings.shopifyExtension?.hideRedoProduct
          ?.checkoutButtonSelector ?? "",
      cartCountModifications:
        formValue.storefront.hideRedoProduct.cartCountModifications.map(
          (x) => ({
            selector: x.selector,
            search: x.search,
            replace: x.replace,
          }),
        ),
      cartPriceModifications:
        formValue.storefront.hideRedoProduct.cartPriceModifications.map(
          (x) => ({
            selector: x.selector,
            search: x.search,
            replace: x.replace,
          }),
        ),
      cartDiscountModifications:
        formValue.storefront.hideRedoProduct.cartDiscountModifications.map(
          (x) => ({
            selector: x.selector,
            search: x.search,
            replace: x.replace,
          }),
        ),
    },
    checkoutExtensionEnabled:
      isAttachmentStrategyCheckoutExtensionEnabled[
        formValue.storefront.attachmentStrategy
      ],
    customPdpCss: formValue.storefront.customPdpCss,
    customToggleCss: formValue.storefront.customToggleCss,
    usingCartAndCheckoutToggle: formValue.storefront.usingCartAndCheckoutToggle,
    autoNavigateToCheckoutAfterTimeout:
      formValue.storefront.autoNavigateToCheckoutAfterTimeout,
    trackRedoProductInventory: formValue.storefront.trackRedoProductInventory,
    lockProduct: formValue.storefront.lockProduct,
    allowNonRedoVendorOnProduct:
      formValue.storefront.allowNonRedoVendorOnProduct,
  };
  team.settings.packageProtectionAutoCheck =
    formValue.storefront.packageProtectionAutoCheck;
  team.settings.modalTextAdjustments = {
    text1: formValue.storefront.checkboxInfoModal.text1,
    title1: formValue.storefront.checkboxInfoModal.title1,
    text2: formValue.storefront.checkboxInfoModal.text2,
    title2: formValue.storefront.checkboxInfoModal.title2,
    text3: formValue.storefront.checkboxInfoModal.text3,
    title3: formValue.storefront.checkboxInfoModal.title3,
  };
  team.settings.infoModal = {
    ...team.settings.infoModal,
    cartIconEnabled: formValue.storefront.checkboxInfoModal.cartIconEnabled,
    checkoutIconEnabled:
      formValue.storefront.checkboxInfoModal.checkoutIconEnabled,
    version: formValue.storefront.checkboxInfoModal.version,
    logo: formValue.storefront.checkboxInfoModal.newLogo
      ? formValue.storefront.checkboxInfoModal.newLogo.toString()
      : "",
    sideImage: formValue.storefront.checkboxInfoModal.sideImage
      ? formValue.storefront.checkboxInfoModal.sideImage.toString()
      : "",
  };
  team.settings.modalLogo = formValue.storefront.checkboxInfoModal.logo
    ? formValue.storefront.checkboxInfoModal.logo.toString()
    : "";

  return team;
};
